import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { SubscribePage } from './pages/SubscribePage';
import { CheckoutPage } from './pages/CheckoutPage';
import { TaskBoardPage } from './pages/TaskBoardPage';
import { SettingsPage } from './pages/SettingsPage';
import { TelegramLinkPage } from './pages/TelegramLinkPage';
import { CommandCenter } from './pages/CommandCenter';
import { ChatPage } from './pages/ChatPage';
import { InboxPage } from './pages/InboxPage';
import { TalentsPage } from './pages/TalentsPage';
import { SkillsPage } from './pages/SkillsPage';
import { SourcesPage } from './pages/SourcesPage';
import { ResearchPage } from './pages/ResearchPage';
import { WorkbenchPage } from './pages/WorkbenchPage';
import { WorkspaceJoinPage } from './pages/WorkspaceJoinPage';
import { SeityBondCommitPage } from './pages/SeityBondCommitPage';
import { VerticalAdminIndexPage } from './pages/VerticalAdminIndexPage';
import { PracticeAdminPage } from './pages/PracticeAdminPage';
import { VerticalAdminPage } from './pages/VerticalAdminPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RequireDirector } from './components/RequireDirector';
import { RequireVerticalOperator } from './components/RequireVerticalOperator';
import { RequireSurface } from './components/RequireSurface';
import { RoleHomeRedirect } from './components/RoleHomeRedirect';
import { DeskPageLoadBoundary } from './components/DeskPageLoadBoundary';

const SeitiatePlusDeskRoute = lazy(() => import('./features/desk/SeitiatePlusDeskRoute'));

function DeskGate() {
  return (
    <RequireSurface surface="desk">
      <DeskPageLoadBoundary>
        <Suspense
          fallback={
            <div className="flex h-full min-h-[40vh] items-center justify-center text-sm text-gray-400">
              Loading…
            </div>
          }
        >
          <SeitiatePlusDeskRoute />
        </Suspense>
      </DeskPageLoadBoundary>
    </RequireSurface>
  );
}

// Seitiate+ — utilitarian web surface over the Seitiate engine.
// Routes (path → component, same order as the <Route path=...> definitions below):
//   /login                              — LoginPage
//   /subscribe                          — Navigate to /subscribe/sensarra
//   /subscribe/:vertical                — SubscribePage
//   / (index)                           — RoleHomeRedirect (customer home: /research; operator/Director: /dashboard)
//   /chat                               — ChatPage
//   /skills                             — SkillsPage
//   /sources                            — SourcesPage
//   /research                           — ResearchPage (customer home)
//   /desk                               — DeskGate
//   /desk/:docId                        — DeskGate
//   /inbox                              — RequireSurface(inbox) → InboxPage
//   /dashboard                          — RequireSurface(dashboard) → DashboardPage
//   /board                              — RequireSurface(board) → TaskBoardPage
//   /talents                            — RequireSurface(talents) → TalentsPage
//   /workbench                          — RequireSurface(workbench) → WorkbenchPage
//   /workbench/assets                   — RequireSurface(workbench) → WorkbenchPage
//   /workbench/recipes                  — RequireSurface(workbench) → WorkbenchPage
//   /workbench/runs                     — RequireSurface(workbench) → WorkbenchPage
//   /workbench/published                — RequireSurface(workbench) → WorkbenchPage
//   /workspaces/join                    — WorkspaceJoinPage
//   /workspaces/join/:token             — WorkspaceJoinPage
//   /seity-bond/commit                  — SeityBondCommitPage
//   /telegram                           — TelegramLinkPage
//   /settings                           — SettingsPage
//   /settings/:sectionId                — SettingsPage
//   /settings/:sectionId/:subSectionId  — SettingsPage
//   /admin/verticals                    — RequireSurface(vertical_admin) → VerticalAdminIndexPage
//   /admin/verticals/:vertical          — RequireVerticalOperator → VerticalAdminPage
//   /command/*                          — RequireDirector → CommandCenter (Command-local routes)
//   *                                   — NotFoundPage
export default function App() {
  return (
    <Routes>
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/login" element={<LoginPage />} />
      {/* Public subscribe page (works signed-out and signed-in) — funnel Phase C + H. */}
      <Route path="/subscribe" element={<Navigate to="/subscribe/sensarra" replace />} />
      <Route path="/subscribe/:vertical" element={<SubscribePage />} />
      <Route element={<AppShell />}>
        {/* Role-aware home: customer → /research, operator/Director → /dashboard. */}
        <Route index element={<RoleHomeRedirect />} />
        {/* Customer-safe surfaces — visible to every authenticated caller. */}
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/skills" element={<SkillsPage />} />
        <Route path="/sources" element={<SourcesPage />} />
        <Route path="/research" element={<ResearchPage />} />
        {/* The merged writing desk stays fail-closed while the complete vehicle is
            unavailable. A server-issued `desk` surface capability is the only
            route in; nav hiding alone is never treated as access control. */}
        <Route path="/desk" element={<DeskGate />} />
        <Route path="/desk/:docId" element={<DeskGate />} />
        {/* Operator+ surfaces — gated to the backend visible-surface set (nav hiding
            is not enough: a customer could otherwise type the path directly). */}
        <Route
          path="/inbox"
          element={<RequireSurface surface="inbox"><InboxPage /></RequireSurface>}
        />
        <Route
          path="/dashboard"
          element={<RequireSurface surface="dashboard"><DashboardPage /></RequireSurface>}
        />
        <Route
          path="/board"
          element={<RequireSurface surface="board"><TaskBoardPage /></RequireSurface>}
        />
        <Route
          path="/talents"
          element={<RequireSurface surface="talents"><TalentsPage /></RequireSurface>}
        />
        <Route
          path="/workbench"
          element={<RequireSurface surface="workbench"><WorkbenchPage /></RequireSurface>}
        />
        <Route
          path="/workbench/assets"
          element={<RequireSurface surface="workbench"><WorkbenchPage /></RequireSurface>}
        />
        <Route
          path="/workbench/recipes"
          element={<RequireSurface surface="workbench"><WorkbenchPage /></RequireSurface>}
        />
        <Route
          path="/workbench/runs"
          element={<RequireSurface surface="workbench"><WorkbenchPage /></RequireSurface>}
        />
        <Route
          path="/workbench/published"
          element={<RequireSurface surface="workbench"><WorkbenchPage /></RequireSurface>}
        />
        <Route path="/workspaces/join" element={<WorkspaceJoinPage />} />
        <Route path="/workspaces/join/:token" element={<WorkspaceJoinPage />} />
        <Route path="/seity-bond/commit" element={<SeityBondCommitPage />} />
        <Route path="/telegram" element={<TelegramLinkPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/:sectionId" element={<SettingsPage />} />
        <Route path="/settings/:sectionId/:subSectionId" element={<SettingsPage />} />
        <Route path="/admin/practices" element={<RequireSurface surface="practice_admin"><PracticeAdminPage /></RequireSurface>} />
        <Route
          path="/admin/verticals"
          element={
            <RequireSurface surface="vertical_admin">
              <VerticalAdminIndexPage />
            </RequireSurface>
          }
        />
        {/* Vertical management center (customers + pricing + compute/costs).
            Director (any vertical) or a recognized operator of this vertical;
            per-vertical operator authorization is re-enforced backend-side. */}
        <Route
          path="/admin/verticals/:vertical"
          element={
            <RequireVerticalOperator>
              <VerticalAdminPage />
            </RequireVerticalOperator>
          }
        />
        {/* Command Center — CTO-only. The guard itself is unchanged; it now wraps
            the whole /command/* subtree, so every Command child route keeps the
            same Director check and a non-Director cannot enter by typing one. */}
        <Route
          path="/command/*"
          element={
            <RequireDirector>
              <CommandCenter />
            </RequireDirector>
          }
        />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
